import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input } from 'ncoded-component-library';
import SearchIcon from 'icons/Search.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { NEW_WINDOW_PROPS } from 'data/global';
import FileRowPlaceholder from 'components/FileAccessSelector/components/FileRowPlaceholder';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  getIsChecked,
  getIsMixed,
} from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess/utils';
import {
  getHasElementsInArray,
  getIsArrayEmpty,
  getIsNodeFolder,
  getShowNodeHasBuyerFiles,
  isEnterPressed,
} from 'utils';
import StageFileRow from '../StageFileRow';
import StageFilesEmptyState from '../StageFilesEmptyState';
import WorkflowGuideAnchor from '../WorkflowGuideAnchor';
import { FILES_SELECTOR } from '../../data';
import { CheckboxGroupPlaceholders } from '../WorkflowTemplate/checkboxplaceholders';
import useStageNodeMeta from '../../hooks/useStageNodeMeta';
import useCheckedFilesField from '../../hooks/useCheckedFilesField';
import {
  StageFilesTree as StageFilesTreeData,
  getIsNodeDisabled,
  getNodeSelectionState,
  getSearchMatches,
  toggleNodeSelection,
} from '../../utils/stageFiles';

import './StageFilesTree.styles.scss';

type StageFilesTreeProps = {
  className?: string;
  tree: StageFilesTreeData;
  dataRoomUrl: string;
  isLoading?: boolean;
  getDisabledReason: (node: DataRoomNode) => string | undefined;
  onSearchChange?: (isSearching: boolean) => void;
};

const StageFilesTree: React.FC<StageFilesTreeProps> = (props) => {
  const {
    className,
    tree,
    dataRoomUrl,
    isLoading,
    getDisabledReason,
    onSearchChange,
  } = props;

  const { rootNodes, childrenByParent, nodesById } = tree;

  const [searchString, setSearchString] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set<string>());
  // Folders a search opened for its matches, then closed by hand; a new search opens them again.
  const [collapsedMatchIds, setCollapsedMatchIds] = useState(new Set<string>());

  const { checkedFilesIds, setCheckedFilesIds } = useCheckedFilesField();

  const { t } = useTranslation();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const { getNodeMeta } = useStageNodeMeta();

  const checkedIds = useMemo(() => new Set(checkedFilesIds), [checkedFilesIds]);

  const search = searchString.trim();

  const { matchedIds, ancestorIds } = useMemo(
    () =>
      search
        ? getSearchMatches(tree, search)
        : { matchedIds: new Set<string>(), ancestorIds: new Set<string>() },
    [search, tree],
  );

  const toggleExpanded = useCallback(
    (id: string, isOpenedBySearch: boolean) =>
      (isOpenedBySearch ? setCollapsedMatchIds : setExpandedIds)((prev) => {
        const next = new Set(prev);

        if (next.has(id)) next.delete(id);
        else next.add(id);

        return next;
      }),
    [],
  );

  const toggleSelection = useCallback(
    (node: DataRoomNode) =>
      setCheckedFilesIds(toggleNodeSelection(node, checkedFilesIds, tree)),
    [checkedFilesIds, setCheckedFilesIds, tree],
  );

  const renderNodes = (
    nodes: DataRoomNode[],
    depth: number,
    isInsideMatch: boolean,
  ): React.ReactNode[] =>
    nodes
      .filter(
        ({ _id }) =>
          !search ||
          isInsideMatch ||
          matchedIds.has(_id) ||
          ancestorIds.has(_id),
      )
      .map((node) => {
        const { _id } = node;

        const isFolder = getIsNodeFolder(node);
        const isOpenedBySearch = !!search && ancestorIds.has(_id);
        const isExpanded = isOpenedBySearch
          ? !collapsedMatchIds.has(_id)
          : expandedIds.has(_id);
        const children = childrenByParent[_id];

        const state = getNodeSelectionState(node, checkedIds, nodesById);
        const disabled = getIsNodeDisabled(node);

        return (
          <li key={_id}>
            <StageFileRow
              node={node}
              depth={depth}
              hasChevron
              isExpanded={isExpanded}
              meta={getNodeMeta(node)}
              note={
                getShowNodeHasBuyerFiles(node)
                  ? t('onlySellSideFilesWillBeShared')
                  : undefined
              }
              disabledReason={getDisabledReason(node)}
              onClick={
                isFolder
                  ? () => toggleExpanded(_id, isOpenedBySearch)
                  : disabled
                  ? undefined
                  : () => toggleSelection(node)
              }
              action={
                <Checkbox
                  key={state}
                  checked={getIsChecked(state)}
                  disabled={disabled}
                  className={classNames({ mixed: getIsMixed(state) })}
                  onChange={() => toggleSelection(node)}
                />
              }
            />

            {isFolder && isExpanded && (
              <ul>
                {!children ? (
                  <li className="stage-files-tree__placeholder">
                    <FileRowPlaceholder />
                  </li>
                ) : getHasElementsInArray(children) ? (
                  renderNodes(
                    children,
                    depth + 1,
                    isInsideMatch || matchedIds.has(_id),
                  )
                ) : (
                  <li className="stage-files-tree__empty-folder">
                    {t('emptyFolder')}
                  </li>
                )}
              </ul>
            )}
          </li>
        );
      });

  const classes = classNames('stage-files-tree', className);

  if (!isLoading && getIsArrayEmpty(rootNodes)) {
    return (
      <StageFilesEmptyState
        title={t('dataRoomHasNoFilesYet')}
        description={t('dataRoomHasNoFilesYetDescription')}
      >
        <a href={dataRoomUrl} {...NEW_WINDOW_PROPS}>
          {t('openTheDataRoom')}
        </a>
      </StageFilesEmptyState>
    );
  }

  const renderedNodes = renderNodes(rootNodes, 0, false);

  return (
    <div className={classes}>
      <Input
        className="stage-files-tree__search"
        value={searchString}
        prefixNode={<SearchIcon />}
        // Counts what has loaded so far, so the number fills in as folders arrive.
        disabled={isLoading}
        placeholder={t('searchFilesAndFolders')}
        // The search sits inside the stage form, so Enter must not submit it.
        onKeyDown={(e) => {
          if (isEnterPressed(e)) e.preventDefault();
        }}
        onChange={(e) => {
          setSearchString(e.target.value);
          setCollapsedMatchIds(new Set());
          onSearchChange?.(!!e.target.value.trim());
        }}
      />

      {!isAbovePhablet && (
        // Mobile has no Files title, so the tour's files step lives here.
        <p className="stage-files-tree__hint">
          {t('stageFilesSortHint')}
          <WorkflowGuideAnchor
            id={FILES_SELECTOR}
            title="pickStageFiles"
            description="pickStageFilesDescription"
          />
        </p>
      )}

      {/* Only the list waits for the stage, so the tour's anchor above is there from the start. */}
      {isLoading ? (
        <CheckboxGroupPlaceholders itemCount={4} />
      ) : getHasElementsInArray(renderedNodes) ? (
        <div className="stage-files-tree__list">
          <ul>{renderedNodes}</ul>
        </div>
      ) : (
        <p className="stage-files-tree__no-results">
          {t('noFilesMatchSearch')}
        </p>
      )}
    </div>
  );
};

export default StageFilesTree;
